import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API_CONFIG } from '../../config/api';
import { User, CreateUserRequest, UpdateUserRequest } from '../../types/User';
import { ApiResponse } from '../../types/ApiResponse';
import { readApiResponse } from '../../lib/utils/apiResponse';

const API_BASE_URL = API_CONFIG.API_URL;

interface UsersState {
  admins: User[];
  customers: User[];
  deactivatedCustomers: User[];
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  adminPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  customerPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  deactivatedPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const initialState: UsersState = {
  admins: [],
  customers: [],
  deactivatedCustomers: [],
  currentUser: null,
  loading: false,
  error: null,
  adminPagination: { currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
  customerPagination: { currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
  deactivatedPagination: { currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Fetch admins
export const fetchAdmins = createAsyncThunk(
  'users/fetchAdmins',
  async (params: { pageNumber?: number; pageSize?: number; searchTerm?: string; sortBy?: string } | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);

      const url = `${API_BASE_URL}/admin/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to fetch admins');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch admins');
    }
  }
);

// Fetch admin by ID
export const fetchAdminById = createAsyncThunk(
  'users/fetchAdminById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/${id}`, { headers: getAuthHeaders() });
      const data: ApiResponse = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to fetch admin');
      }
      return data.result as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch admin');
    }
  }
);

// Create admin
export const createAdmin = createAsyncThunk(
  'users/createAdmin',
  async (userData: CreateUserRequest, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      const data: ApiResponse = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to create admin');
      }
      return data.result as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create admin');
    }
  }
);

// Update admin
export const updateAdmin = createAsyncThunk(
  'users/updateAdmin',
  async ({ id, data }: { id: string; data: UpdateUserRequest }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      const apiResponse: ApiResponse = await readApiResponse(response);
      if (!response.ok || !apiResponse.isSuccess) {
        return rejectWithValue(apiResponse.errorMessages?.join(', ') || 'Failed to update admin');
      }
      return apiResponse.result as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update admin');
    }
  }
);

// Remove admin role
export const removeAdminRole = createAsyncThunk(
  'users/removeAdminRole',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data: ApiResponse = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to remove admin role');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to remove admin role');
    }
  }
);

// Fetch customers (active only)
export const fetchCustomers = createAsyncThunk(
  'users/fetchCustomers',
  async (params: { pageNumber?: number; pageSize?: number; searchTerm?: string; sortBy?: string } | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);

      const url = `${API_BASE_URL}/admin/customers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to fetch customers');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch customers');
    }
  }
);

// Fetch deactivated customers
export const fetchDeactivatedCustomers = createAsyncThunk(
  'users/fetchDeactivatedCustomers',
  async (params: { pageNumber?: number; pageSize?: number; searchTerm?: string; sortBy?: string } | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);

      const url = `${API_BASE_URL}/admin/customers/deactivated${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to fetch deactivated customers');
      }
      return data.result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch deactivated customers');
    }
  }
);

// Fetch customer by ID
export const fetchCustomerById = createAsyncThunk(
  'users/fetchCustomerById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/customers/${id}`, { headers: getAuthHeaders() });
      const data: ApiResponse = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to fetch customer');
      }
      return data.result as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch customer');
    }
  }
);

// Update customer
export const updateCustomer = createAsyncThunk(
  'users/updateCustomer',
  async ({ id, data }: { id: string; data: UpdateUserRequest }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/customers/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      const apiResponse: ApiResponse = await readApiResponse(response);
      if (!response.ok || !apiResponse.isSuccess) {
        return rejectWithValue(apiResponse.errorMessages?.join(', ') || 'Failed to update customer');
      }
      return apiResponse.result as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update customer');
    }
  }
);

// Deactivate customer
export const deactivateCustomer = createAsyncThunk(
  'users/deactivateCustomer',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/customers/${id}/deactivate`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data: ApiResponse = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to deactivate customer');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to deactivate customer');
    }
  }
);

// Activate customer
export const activateCustomer = createAsyncThunk(
  'users/activateCustomer',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/customers/${id}/activate`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data: ApiResponse = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to activate customer');
      }
      return data.result as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to activate customer');
    }
  }
);

// Set customer as admin
export const setCustomerAsAdmin = createAsyncThunk(
  'users/setCustomerAsAdmin',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/customers/${id}/set-admin`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data: ApiResponse = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.join(', ') || 'Failed to set user as admin');
      }
      return { id, user: data.result as User };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to set user as admin');
    }
  }
);


const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch admins
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.admins = action.payload?.Data || action.payload?.data || [];
        if (action.payload?.Pagination || action.payload?.pagination) {
          const p = action.payload.Pagination || action.payload.pagination;
          state.adminPagination = {
            currentPage: p.currentPage || p.CurrentPage,
            pageSize: p.pageSize || p.PageSize,
            totalCount: p.totalCount || p.TotalCount,
            totalPages: p.totalPages || p.TotalPages,
          };
        }
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch admins';
      })

      // Fetch admin by ID
      .addCase(fetchAdminById.fulfilled, (state, action: PayloadAction<User>) => {
        state.currentUser = action.payload;
      })

      // Create admin
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.admins.unshift(action.payload);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create admin';
      })

      // Update admin
      .addCase(updateAdmin.fulfilled, (state, action: PayloadAction<User>) => {
        const index = state.admins.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.admins[index] = action.payload;
        }
      })

      // Remove admin role
      .addCase(removeAdminRole.fulfilled, (state, action: PayloadAction<string>) => {
        state.admins = state.admins.filter((a) => a.id !== action.payload);
      })

      // Fetch customers
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.customers = action.payload?.Data || action.payload?.data || [];
        if (action.payload?.Pagination || action.payload?.pagination) {
          const p = action.payload.Pagination || action.payload.pagination;
          state.customerPagination = {
            currentPage: p.currentPage || p.CurrentPage,
            pageSize: p.pageSize || p.PageSize,
            totalCount: p.totalCount || p.TotalCount,
            totalPages: p.totalPages || p.TotalPages,
          };
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch customers';
      })

      // Fetch deactivated customers
      .addCase(fetchDeactivatedCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeactivatedCustomers.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.deactivatedCustomers = action.payload?.Data || action.payload?.data || [];
        if (action.payload?.Pagination || action.payload?.pagination) {
          const p = action.payload.Pagination || action.payload.pagination;
          state.deactivatedPagination = {
            currentPage: p.currentPage || p.CurrentPage,
            pageSize: p.pageSize || p.PageSize,
            totalCount: p.totalCount || p.TotalCount,
            totalPages: p.totalPages || p.TotalPages,
          };
        }
      })
      .addCase(fetchDeactivatedCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch deactivated customers';
      })

      // Fetch customer by ID
      .addCase(fetchCustomerById.fulfilled, (state, action: PayloadAction<User>) => {
        state.currentUser = action.payload;
      })

      // Update customer
      .addCase(updateCustomer.fulfilled, (state, action: PayloadAction<User>) => {
        const index = state.customers.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
      })

      // Deactivate customer
      .addCase(deactivateCustomer.fulfilled, (state, action: PayloadAction<string>) => {
        state.customers = state.customers.filter((c) => c.id !== action.payload);
      })

      // Activate customer
      .addCase(activateCustomer.fulfilled, (state, action: PayloadAction<User>) => {
        // Remove from deactivated list
        state.deactivatedCustomers = state.deactivatedCustomers.filter((c) => c.id !== action.payload.id);
      })

      // Set customer as admin
      .addCase(setCustomerAsAdmin.fulfilled, (state, action: PayloadAction<{ id: string; user: User }>) => {
        state.customers = state.customers.filter((c) => c.id !== action.payload.id);
        state.admins.unshift(action.payload.user);
      });
  },
});

export const { clearCurrentUser, clearError } = usersSlice.actions;
export default usersSlice.reducer;
